import {useState, useRef, useEffect, useId} from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import {sendEventToGA} from "./../utils.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCaretDown, faQuestionCircle, faSearch} from "@fortawesome/free-solid-svg-icons";
import {library as fontAwesomeLibrary} from "@fortawesome/fontawesome-svg-core";


/**
 * Search Bar Component
 * Reusable search input component that allows users to enter a query and
 * navigate to a search results page.
 *
 * @param props.currentQuery (The currently entered query, from react state)
 * @param props.setCurrentQuery (Function to set react state)
 * @param props.navigate (function that lets us navigate to another page)
 * @param props.placeholder (Placeholder text on the input element)
 * @param props.centered (whether the element is being centered. Still requires text-align: center
 * on the parent element).
 * @param props.noControls (don't display controls above or below the search bar)
 * @param props.whiteBg (searchbar is being displayed on white background)
 * @return JSX for displaying in the page
 */
export function SearchBar(props) {
    // Required props variables
    const {
        setCurrentQuery, navigate, isNonLandingPageBar,
        placeholder, centered, noControls, whiteBg,
    } = props;

    const [isFocused, setIsFocused] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionMode, setSuggestionMode] = useState("global");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const inputRef = useRef(null);
    const location = useLocation();
    const blurTimeoutRef = useRef(null);

    // Optional props variables
    const currentQuery = props.currentQuery === '*' ? '' : props.currentQuery;
    const shortLength = props.shortLength || false;
    const mediumLength = props.mediumLength || false;
    const source_component = props.source_component || "search_bar";


    // Field-aware suggestion config: maps a field name to its endpoint builder
    // and the response shape. Add new entries here to support more fields.
    const FIELD_SUGGESTION_CONFIG = {
        mission: {
            endpoint: (partial) =>
                `/heliodata/app/get_matching_missions/${encodeURIComponent(partial)}/`,
            extractItems: (data) => data.missions || [],
        },
        instrument: {
            endpoint: (partial) =>
                `/heliodata/app/get_matching_instrument/${encodeURIComponent(partial)}/`,
            extractItems: (data) => data.results || [],
        },
    };

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    // Get suggestions from backend
    useEffect(() => {
        if (!inputRef.current) return;

        const cursorPos = inputRef.current.selectionStart ?? currentQuery.length;
        const activeSegment = getActiveFieldSegment(currentQuery, cursorPos);

        // Field-aware suggestions: fire whenever the cursor is inside a
        // mission:"..." or instrument:"..." token, on any page.
        if (activeSegment && activeSegment.partial.length >= 1) {
            const config = FIELD_SUGGESTION_CONFIG[activeSegment.field];
            if (!config) {
                // Field has no configured suggestion endpoint; clear and bail.
                setSuggestions([]);
                setSuggestionMode("global");
                return;
            }

            const controller = new AbortController();
            const delay = setTimeout(async () => {
                try {
                    const res = await axios.get(
                        config.endpoint(activeSegment.partial),
                        { signal: controller.signal }
                    );
                    const items = config.extractItems(res.data);
                    const names = [...new Set(
                        items.map(item => item.shortName).filter(Boolean)
                    )];
                    setSuggestions(names);
                    setSuggestionMode(activeSegment.field);
                    setHighlightedIndex(-1);
                } catch (err) {
                    // Aborted requests are expected when the user keeps typing;
                    // don't clear suggestions in that case (the next request is
                    // already on its way).
                    if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
                    setSuggestions([]);
                    setSuggestionMode("global");
                }
            }, 250);
            return () => {
                clearTimeout(delay);
                controller.abort();
            };
        }

        // Outside field tokens: fall back to global suggester, landing page only.
        if (location.pathname !== "/") {
            setSuggestions([]);
            setSuggestionMode("global");
            return;
        }

        // Skip the global suggester when the query is just placeholder field
        // templates (e.g. ' mission:""' from clicking the Mission button).
        // These have no content to suggest against and waste a backend call.
        const stripped = currentQuery?.replace(/\w+:""/g, "").trim();
        if (stripped && stripped.length === 0) {
            setSuggestions([]);
            setSuggestionMode("global");
            return;
        }

        const delay = setTimeout(() => {
            if (currentQuery && currentQuery.length > 1) {
                axios.get(`/heliodata/app/get_suggestions?q=${encodeURIComponent(currentQuery)}`)
                    .then(res => {
                       setSuggestions(res.data.suggestions || []);
                       setSuggestionMode("global");
                    })
                    .catch(() => setSuggestions([]));
            } else {
                setSuggestions([]);
                setSuggestionMode("global");
            }
        }, 250);
        return () => clearTimeout(delay);
    }, [currentQuery, location.pathname]);


    // ---------------------------------------------------------------------------
    // 1) applyFieldSuggestion -- returns the rebuilt query (or null)
    // ---------------------------------------------------------------------------
    const applyFieldSuggestion = (suggestionText, appendComma) => {
        if (!inputRef.current) return null;
        const cursorPos = inputRef.current.selectionStart ?? currentQuery.length;
        const seg = getActiveFieldSegment(currentQuery, cursorPos);
        if (!seg) return null;

        const before = currentQuery.slice(0, seg.replaceStart);
        const after = currentQuery.slice(seg.replaceEnd);
        const insertion = appendComma ? `${suggestionText}, ` : suggestionText;
        const newQuery = before + insertion + after;

        setCurrentQuery(newQuery);
        setSuggestions([]);
        setHighlightedIndex(-1);

        const newCursorPos = before.length + insertion.length;
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);

        return newQuery;
    };



    const getActiveFieldSegment = (query, cursorPos) => {
        const fieldPattern = /(mission|instrument):"([^"]*)"/g;
        let match;
        while ((match = fieldPattern.exec(query)) !== null) {
            const valueStart = match.index + match[1].length + 2;
            const valueEnd = valueStart + match[2].length;

            if (cursorPos >= valueStart && cursorPos <= valueEnd) {
                const value = match[2];
                const cursorOffset = cursorPos - valueStart;
                const segmentBefore = value.slice(0, cursorOffset);
                const lastCommaIdx = segmentBefore.lastIndexOf(",");
                const segmentStart = valueStart + (lastCommaIdx + 1);

                const rawPartial = query.slice(segmentStart, cursorPos);
                const partial = rawPartial.replace(/^\s+/, "");
                const leadingSpaceLen = rawPartial.length - partial.length;

                return {
                    field: match[1],
                    partial,
                    replaceStart: segmentStart + leadingSpaceLen,
                    replaceEnd: cursorPos,
                };
            }
        }
        return null;
    };

    const insertFieldAndSetCursor = (e, insertion) => {
        const newQuery = currentQuery + insertion;
        setCurrentQuery(newQuery);

        setTimeout(() => {
            if(inputRef && inputRef.current) {
                const position = newQuery.length - 1;
                inputRef.current.focus();
                inputRef.current.setSelectionRange(position, position);
            }
        }, 0);
    };

    // Resolve comma-separated mission terms to canonical {hcutId, shortName}.
    const resolveMissionTerms = async (terms) => {
        const perTerm = await Promise.all(terms.map(async (rawTerm) => {
            const cleaned = rawTerm.trim().replace(/,$/, "");
            if (!cleaned) return { cleaned: "", chosen: null };
            try {
                 const res = await axios.get(
                    `/heliodata/app/get_matching_missions/${encodeURIComponent(cleaned)}/`
                );
                const missions = res.data.missions || [];
                const deduped = [];
                const seen = new Set();
                for (const m of missions) {
                    if (seen.has(m.hcutId)) continue;
                    seen.add(m.hcutId);
                    deduped.push(m);
                }
                let chosen = null;
                if (deduped.length === 1) {
                    chosen = deduped[0];
                } else if (deduped.length > 1) {
                    chosen = deduped.find(
                        m => m.shortName?.toLowerCase() === cleaned.toLowerCase()
                    ) || null;
                }
                return { cleaned, chosen };
            } catch (err) {
                console.error("Failed to fetch mission", cleaned, err);
                return { cleaned, chosen: null };
           }
        }));

        const resolved = [];
        const unresolved = [];
        const seenIds = new Set();
        for (const { cleaned, chosen } of perTerm) {
            if (!cleaned) continue;
            if (chosen && !seenIds.has(chosen.hcutId)) {
                seenIds.add(chosen.hcutId);
                resolved.push({ hcutId: chosen.hcutId, shortName: chosen.shortName });
            } else if (!chosen) {
                unresolved.push(cleaned);
            }
        }
        return { resolved, unresolved };
    };

// Resolve comma-separated instrument terms; optionally keep only instruments
// belonging to one of the given mission short names.
const resolveInstrumentTerms = async (terms, missionShortNames = null) => {
    const wanted = missionShortNames
        ? new Set(missionShortNames.map(s => s.toLowerCase()))
        : null;

    const perTerm = await Promise.all(terms.map(async (rawTerm) => {
        const cleaned = rawTerm.trim().replace(/,$/, "");
        if (!cleaned) return { cleaned: "", instruments: [] };
        try {
            const res = await axios.get(
                `/heliodata/app/get_matching_instrument/${encodeURIComponent(cleaned)}/`
            );
            return { cleaned, instruments: res.data.results || [] };
        } catch (err) {
            console.error("Failed to fetch instrument", cleaned, err);
            return { cleaned, instruments: [] };
        }
    }));

    const hcutIds = new Set();
    const shortNames = new Set();
    const unresolved = [];

    for (const { cleaned, instruments } of perTerm) {
        if (!cleaned) continue;

        // If we have a mission context, keep only instruments whose associated
        // missions include one of the wanted mission short names.
        const filtered = wanted
            ? instruments.filter(inst =>
                (inst.missions || []).some(m =>
                    m.shortName && wanted.has(m.shortName.toLowerCase())
                )
              )
            : instruments;

        if (filtered.length === 0) {
            unresolved.push(cleaned);
            continue;
        }
        for (const inst of filtered) {
            hcutIds.add(inst.hcutId);
            if (inst.shortName) shortNames.add(inst.shortName);
        }
    }
    return { hcutIds: [...hcutIds], shortNames: [...shortNames], unresolved };
};

// ---------------------------------------------------------------------------
// Replacement for the `searchCurrentQuery` block in searchbar.js
//
// Delete the existing `const searchCurrentQuery = props.searchCurrentQuery ||
// (async (index) => { ... })` and paste everything below in its place. Keep
// resolveMissionTerms() and resolveInstrumentTerms() exactly as they are --
// they are reused here unchanged.
// ---------------------------------------------------------------------------


/**
 * Split a raw query into an ordered token list, preserving the user's layout:
 *   {type: "field", field, value, raw}   for  name:"value"
 *   {type: "text",  value}               for free-text between/around fields
 *
 * Module-level helper -- no component state needed.
 */
function parseQueryTokens(query) {
    const fieldPattern = /(\w+):"([^"]*)"/g;
    const tokens = [];
    let lastIndex = 0;
    let match;

    while ((match = fieldPattern.exec(query)) !== null) {
        const gap = query.slice(lastIndex, match.index).trim();
        if (gap) tokens.push({type: "text", value: gap});

        tokens.push({
            type: "field",
            field: match[1],
            value: match[2],
            raw: match[0],
        });
        lastIndex = match.index + match[0].length;
    }

    const tail = query.slice(lastIndex).trim();
    if (tail) tokens.push({type: "text", value: tail});

    return tokens;
}


// ===========================================================================
// Everything below goes INSIDE the SearchBar component (it closes over
// resolveMissionTerms / resolveInstrumentTerms / navigate / setCurrentQuery).
// ===========================================================================

/**
 * Per-field rewriters. A resolver returns:
 *   {queryPart}  -- what this token becomes in the Solr query segment
 *   {facet}      -- optional "key=value" pair for the facet URL segment
 *   {error}      -- optional user-facing message; aborts and shows a 0-result page
 *
 * Fields with no entry here (authors, variables, title, cadence_cat, has_api,
 * variable_shortnames, ...) are passed through verbatim, so combining them
 * with mission/instrument/region works with no changes to this file.
 */
const FIELD_RESOLVERS = {
    mission: async (value, ctx) => {
        const terms = value.split(",").map(t => t.trim()).filter(Boolean);
        const {resolved, unresolved} = await resolveMissionTerms(terms);

        if (resolved.length === 0) {
            const label = unresolved[0] || value.trim();
            return {error: `No mission named "${label}"`};
        }

        const shortNames = resolved.map(r => r.shortName);
        // Recorded so a later instrument token can be scoped to these missions.
        ctx.missionShortNames.push(...shortNames);

        return {
            queryPart: `mission:"${shortNames.join(",")}"`,
            facet: `mission_facet_cs=${encodeURIComponent(shortNames.join(","))}`,
        };
    },


    instrument: async (value, ctx) => {
        const terms = value.split(",").map(t => t.trim()).filter(Boolean);
        if (terms.length === 0) return {queryPart: null};

        const scopedTo = ctx.missionShortNames.length ? ctx.missionShortNames : null;
        const {hcutIds, shortNames, unresolved} =
            await resolveInstrumentTerms(terms, scopedTo);

        if (hcutIds.length === 0) {
            const label = unresolved.join(", ") || value.trim();

            // Distinguish "this instrument doesn't exist" from "it exists, but not
            // on the missions you asked for" -- the second is a much more useful
            // message. Costs one extra request, and only on the failure path.
            if (scopedTo) {
                const {hcutIds: unscopedIds} =
                    await resolveInstrumentTerms(unresolved, null);
                if (unscopedIds.length > 0) {
                    return {
                        error: `No instrument named "${label}" on `
                             + `${scopedTo.join(", ")}`,
                    };
                }
            }

            return {error: `No instrument named "${label}"`};
        }

        return {
            queryPart: `instrument_id:"${hcutIds.join(",")}"`,
            facet: `instrument_facet_cs=${encodeURIComponent(shortNames.join(","))}`,
        };
    },

    region: async (value) => {
        // Already a dotted path (sun.chromosphere) -- leave it alone.
        if (value.includes(".")) {
            return {queryPart: `region:"${value}"`};
        }

        const slug = value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

        try {
            const res = await axios.get(
                `/heliodata/app/get_region_parent/${encodeURIComponent(slug)}/`
            );
            const parent = res.data.parentRegions?.[0];
            return {
                queryPart: parent
                    ? `region:"${parent.slug}.${slug}"`
                    : `region:"${slug}"`,
            };
        } catch (err) {
            console.error("Failed to fetch parent region:", err);
            return {queryPart: `region:"${slug}"`};
        }
    },

    doi: async (value) => {
        const doi = value
            .trim()
            .replace(/^https?:\/\/(dx\.)?doi\.org\//i, "")
            .replace(/^(dx\.)?doi\.org\//i, "");
        return {queryPart: `doi:"https://doi.org/${doi}"`};
    },
};

    // Fields resolved in a first pass because later resolvers depend on their
    // output (instrument scoping needs the mission short names).
    const PRIORITY_FIELDS = ["mission"];


    /**
     * Rewrite a raw user query into the query segment + facet segment used by the
     * /search/:query/:page/:facets route. Single pass, order-independent, and it
     * never discards tokens it doesn't recognise.
    */
    const rewriteQuery = async (rawQuery) => {
        const tokens = parseQueryTokens(rawQuery);
        const ctx = {missionShortNames: []};

        const resolveToken = async (tok) => {
            const resolver = FIELD_RESOLVERS[tok.field];
            if (!resolver) {
                tok.resolved = {queryPart: tok.raw};   // unknown field: pass through
                return null;
            }
            tok.resolved = await resolver(tok.value, ctx);
            return tok.resolved?.error || null;
        };

        // Pass 1: priority fields, in query order.
        for (const tok of tokens) {
            if (tok.type !== "field") continue;
            if (!PRIORITY_FIELDS.includes(tok.field)) continue;
            const error = await resolveToken(tok);
            if (error) return {error};
        }

        // Pass 2: everything else.
        for (const tok of tokens) {
            if (tok.type !== "field" || tok.resolved) continue;
            const error = await resolveToken(tok);
            if (error) return {error};
        }

        // Rebuild, preserving the original token order.
        const queryParts = [];
        const facetParts = [];

        for (const tok of tokens) {
            if (tok.type === "text") {
                queryParts.push(tok.value);
                continue;
            }
            if (tok.resolved.queryPart) queryParts.push(tok.resolved.queryPart);
            if (tok.resolved.facet) facetParts.push(tok.resolved.facet);
        }

        return {
            querySegment: queryParts.join(" ") || "*",
            facetSegment: facetParts.join("&"),
        };
    };


    // ---------------------------------------------------------------------------
    // 2) searchCurrentQuery -- optional explicit query
    //
    // searchCurrentQuery(index)            -> search a global suggestion
    // searchCurrentQuery()                 -> search what is in the input
    // searchCurrentQuery(null, "some q")   -> search exactly "some q"
    // ---------------------------------------------------------------------------
    const searchCurrentQuery = props.searchCurrentQuery || (async (index, explicitQuery) => {
        let rawQuery = "";

        if (typeof explicitQuery === "string") {
            rawQuery = explicitQuery.trim();
        } else if ((index !== undefined && index !== null && index !== -1)
                   || (suggestionMode === "global" && highlightedIndex >= 0)) {
            // Only global suggestions replace the whole query. Field suggestions
            // (mission/instrument) are merged into the query, never searched alone.
            const ind = (index !== undefined && index !== null) ? index : highlightedIndex;
            rawQuery = suggestions[ind]?.trim() || "";
        } else if (currentQuery && currentQuery.length > 0) {
            rawQuery = currentQuery.trim();
        }

        setSuggestions([]);
        setHighlightedIndex(-1);

        if (rawQuery.length === 0) return;

        sendEventToGA(`${source_component}__search`, "searches", rawQuery);
        setCurrentQuery("");

        const {querySegment, facetSegment, error} = await rewriteQuery(rawQuery);

        if (error) {
            navigate(`/search/${encodeURIComponent(error)}/1`);
            return;
        }

        if (facetSegment) {
            navigate(`/search/${encodeURIComponent(querySegment)}/1/${facetSegment}`);
        } else {
            navigate(`/search/${encodeURIComponent(querySegment)}/1`);
        }
    });


    // Search list of class names for outer <div> element
    const outerDivClasses = [];

    if(!shortLength) {
        outerDivClasses.push("searchBar");
    }else {
        outerDivClasses.push("searchBarShorter");
        outerDivClasses.push("searchBarShortLength");
    }

    // Set list of class names for the <input> element
    const searchInputClasses = [];

    searchInputClasses.push("searchBarInput");

    if (whiteBg) {
        searchInputClasses.push("searchBarInputWhiteBg ");
    }

    if (shortLength) {
        searchInputClasses.push("searchBarInputShortLength");
    }

    if (mediumLength) {
        searchInputClasses.push("searchBarInputMediumLength");
    }

    // Set callback functions
    const handleChange = (event) => {
        event.preventDefault();
        setCurrentQuery(event.target.value);
    };

    const onKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            if (suggestions.length === 0) return;
            event.preventDefault();
            setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
        } else if (event.key === "ArrowUp") {
            if (suggestions.length === 0) return;
            event.preventDefault();
            setHighlightedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (event.key === "Tab" && suggestionMode !== "global" && highlightedIndex >= 0) {
            event.preventDefault();
            applyFieldSuggestion(suggestions[highlightedIndex], true);
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (suggestionMode !== "global" && highlightedIndex >= 0) {
                // Search the MERGED query right away instead of calling a stale
                // closure in a setTimeout (which searched only the suggestion text).
                const merged = applyFieldSuggestion(suggestions[highlightedIndex], false);
                searchCurrentQuery(null, merged ?? currentQuery);
            } else {
                searchCurrentQuery();
            }
        }
    };

    const handleFocus = (event) => {

        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        setIsFocused(true);
    };

    const handleBlur = (event) => {
        blurTimeoutRef.current = setTimeout(() => {
            setIsFocused(false);
            blurTimeoutRef.current = null;
        }, 200);
    };

    const clearAll = () => {
        setCurrentQuery("");
        setSuggestions([]);
        setHighlightedIndex(-1);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const activateOnKey = (event, callback) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            callback(event);
        }
    };


    // Return JSX
    return (
        <div className={outerDivClasses.join(" ")}>
          <div className="searchSection">
            {!noControls ? <div style={{display: noControls ? "none": ""}}>
            <span className={isNonLandingPageBar ? "searchBarQuickFieldQSFMini" : "searchBarQuickField"}>QUICK SEARCH FIELD:</span>

	      <span
                title="Insert mission quick field filter"
                role="button"
                tabIndex={0}
                aria-label="Insert mission field filter"
                className={isNonLandingPageBar ? "searchBarQuickFieldMini" : "searchBarQuickFieldOption"}
                onClick={(e) => insertFieldAndSetCursor(e, " mission:\"\"")}
                onKeyDown={(e) => activateOnKey(e, () => insertFieldAndSetCursor(e, " mission:\"\""))}>
                Mission
              </span>
	      <span
                title="Insert instrument quick field filter"
                role="button"
                tabIndex={0}
                aria-label="Insert instrument field filter"
                className={isNonLandingPageBar ? "searchBarQuickFieldMini" : "searchBarQuickFieldOption"}
                onClick={(e) => insertFieldAndSetCursor(e, " instrument:\"\"")}
                onKeyDown={(e) => activateOnKey(e, () => insertFieldAndSetCursor(e, " instrument:\"\""))}>
                Instrument
              </span>
              <span
                title="Insert dataset DOI quick field filter"
                role="button"
                tabIndex={0}
                aria-label="Insert dataset DOI field filter"
                className={isNonLandingPageBar ? "searchBarQuickFieldMini" : "searchBarQuickFieldOption"}
                onClick={(e) => insertFieldAndSetCursor(e, " doi:\"\"")}
                onKeyDown={(e) => activateOnKey(e, () => insertFieldAndSetCursor(e, " doi:\"\""))}>
                Dataset DOI
              </span>

              <SearchBarAllFieldsButton
                setCurrentQuery={setCurrentQuery}
                currentQuery={currentQuery}
                isNonLandingPageBar = {isNonLandingPageBar}
                handleSelection= {insertFieldAndSetCursor}
              />

              <span
                title="Clear search input"
                role="button"
                tabIndex={0}
                aria-label="Clear search input"
                className={isNonLandingPageBar ? "searchBarQuickFieldItemMini" : "searchBarQuickFieldItem"}
                onClick={clearAll}
                onKeyDown={(e) => activateOnKey(e, clearAll)}>
                Clear Search
              </span>

              <SearchBarSearchTipsButton isNonLandingPageBar={isNonLandingPageBar} />

            {/* <SearchBarAdvancedButton centered={centered} /> */}

            <div className="SearchSpacer"></div>

            </div> : <div className="colorBar"></div>}
            <div className={`searchBarWrapper ${isFocused ? "searchButtonShowBorder" : ""}`}>
              <div className={"searchBarMarginsInput"} tabIndex="0">
                <input
                  className={`${searchInputClasses.join(" ")} searchBarInputBorder`}
                  type="text"
                  onChange={handleChange}
                  onKeyDown={onKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  value={currentQuery}
                  ref={inputRef}
                  aria-label="Search for data"
                  placeholder={placeholder}/>

                  {isFocused && suggestions.length > 0 && (
                    <ul className="suggestionsDropdown">
                    {suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      id={index}
                      className={index === highlightedIndex ? "suggestionItemHighlighted" : "suggestionItem"}
                      onMouseDown={(e) => {
                          if (suggestionMode !== "global") {
                              applyFieldSuggestion(suggestions[index], false);
                          } else {
                              searchCurrentQuery(e.currentTarget.id);
                          }
                      }}
                    >
                      {suggestion}
                    </li>
                    ))}
                  </ul>
                  )}

              </div>
              <div className="searchBarMarginsButton"><button
                type="button"
                aria-label="Search"
                className={`${shortLength ? "searchButton" : "searchButton"}`}
                onClick={() => searchCurrentQuery()}>
                <FontAwesomeIcon
                        icon={faSearch}
                         />
              </button></div>
            </div>
          </div>
        </div>
    );
}


/**
 * Displays a button that says "All Fields" and when clicked displays the
 * all fields dialog overlay.
 * @param prop.setCurrentQuery (Callback to set the search bar query)
 * @param prop.currentQuery (Current value of the search bar query)
 * @returns JSX element
 */
function SearchBarAllFieldsButton(props) {
    const {setCurrentQuery, currentQuery, isNonLandingPageBar, handleSelection}= props;
    const [allFieldsDialogVisible, setAllFieldsDialogVisible] = useState(false);
    const buttonRef = useRef(null);
    const dialogId = useId();

    fontAwesomeLibrary.add(faCaretDown);

    // Escape closes the dialog and returns focus to the trigger button.
    useEffect(() => {
        if (!allFieldsDialogVisible) return;
        const handleKey = (e) => {
            if (e.key === "Escape") {
                setAllFieldsDialogVisible(false);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [allFieldsDialogVisible]);

    return (
        <span title="Display Information on All Fields">
          <button
            ref={buttonRef}
            type="button"
            aria-expanded={allFieldsDialogVisible}
            aria-controls={dialogId}
            className={isNonLandingPageBar ? "searchBarQuickFieldItemMini" : "searchBarQuickFieldItem"}
            onClick={() => setAllFieldsDialogVisible(!allFieldsDialogVisible)}>
            <FontAwesomeIcon icon={faCaretDown} />&nbsp;All Fields
          </button>

          <SearchBarAllFieldsDialog
            id={dialogId}
            style={{display: allFieldsDialogVisible ? "" : "none"}}
            visible={allFieldsDialogVisible}
            buttonRef={buttonRef}
            setCurrentQuery={setCurrentQuery}
            currentQuery={currentQuery}
            onClickOutside={() => setAllFieldsDialogVisible(false)}
            handleSelection={handleSelection} />
        </span>
    );
}


/**
 * Displays the "All Fields" dialog overlay that lets users view a list of field
 * filter terms, and read info about them.
 * @param props.onClickOutside (Callback for when user clicks outside this div)
 * @param props.buttonRef (Callback to button that hides/shows this div)
 * @param prop.setCurrentQuery (Callback to set the search bar query)
 * @param prop.currentQuery (Current value of the search bar query)
 * @returns JSX element
 */
function SearchBarAllFieldsDialog(props) {

    const {id, onClickOutside, buttonRef, currentQuery, setCurrentQuery, handleSelection} = props;
    const [displayField, setDisplayedField] = useState("has_api");
    const wrapperRef = useRef(null);

    useOutsideAlerter(wrapperRef, buttonRef, onClickOutside);

    const fieldNames = [
        {label: "API Capability", key:"has_api"},
        {label: "Authors", key: "authors"},
        {label: "Cadence Category", key:"cadence_cat"},
        {label: "Dataset DOI", key: "doi"},
        {label: "Description", key: "description"},
        {label: "FTP Capability", key: "has_ftp"},
        {label: "HelioCloud S3", key: "has_heliocloud_s3"},
        {label: "HTTPS Capability", key: "has_https"},
	{label: "Instrument", key: "instrument"},
	{label: "Mission", key: "mission"},
        {label: "Scientific Region", key: "region"},
        {label: "Title", key: "title"},
        {label: "Variables", key: "variables"},
        {label: "Variables (Short Names)", key: "variable_shortnames"},
    ];

    const fieldButtons = fieldNames.map((fieldObj, index) => {
        const classNames = ["searchBarAllFieldsDialogLeftItem"];

        if (displayField === fieldObj.key) {
            classNames.push("searchBarAllFieldsDialogLeftItemSelected");
        }

        return (
            <div
              key={index}
              role="button"
              tabIndex={0}
              aria-label={`Insert ${fieldObj.label} field filter`}
              onMouseEnter={() => setDisplayedField(fieldObj.key)}
              onFocus={() => setDisplayedField(fieldObj.key)}
              className={classNames.join(" ")}
              onClick={(e) => handleSelection(e, ` ${fieldObj.key}:""`)}
              onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelection(e, ` ${fieldObj.key}:""`);
                  }
              }}>
              {fieldObj.label}
            </div>
        );
    });

    const fieldInfo = getAllFieldsInfoForField(displayField);

    return (
        <div
          id={id}
          ref={wrapperRef}
          role="dialog"
          aria-label="All search fields"
          style={props.style}
          className="searchBarOverlayDialog searchBarAllFieldsDialog">
          <div className="searchBarAllFieldsDialogLeft">
            <strong>Fields</strong>

            <div className="searchBarAllFieldsDialogLeftList">
              {fieldButtons}
            </div>
          </div>

          <div className="searchBarAllFieldsDialogRight">
            {fieldInfo}
        </div>
        </div>
    );
}


/**
 * Displays a "Search Tips" button that opens a dropdown explaining how to
 * use the search bar. Mirrors the All Fields button/dialog pattern.
 * @param props.isNonLandingPageBar (use compact styling for non-landing bars)
 * @returns JSX element
 */
function SearchBarSearchTipsButton({isNonLandingPageBar}) {
    const [visible, setVisible] = useState(false);
    const containerRef = useRef(null);
    const toggleRef = useRef(null);

    const toggle = () => setVisible((prev) => !prev);
    const close = () => {
        setVisible(false);
        if (toggleRef.current) {
            toggleRef.current.focus();
        }
    };

    return (
        <span
          ref={containerRef}
          className={isNonLandingPageBar ? "searchBarSearchTipsMini" : "searchBarSearchTips"}
          title="Show search tips">
          <span
            ref={toggleRef}
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            aria-expanded={visible}
            aria-controls="searchBarSearchTipsDialog"
            className="searchBarSearchTipsToggle"
            onClick={toggle}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle();
                }
            }}>
            Search Tips
          </span>

          <SearchBarSearchTipsDialog
            style={{display: visible ? "" : "none"}}
            visible={visible}
            buttonRef={containerRef}
            onClickOutside={() => setVisible(false)}
            onClose={close} />
        </span>
    );
}


/**
 * The "Search Tips" dropdown overlay. Contains static guidance text on how
 * to use the search bar and quick search fields.
 * @param props.onClickOutside (Callback for when user clicks outside this div)
 * @param props.onClose (Callback to close the dialog and restore focus)
 * @param props.visible (Whether the dialog is currently shown)
 * @param props.buttonRef (Ref to the toggle so outside-click ignores it)
 * @returns JSX element
 */
function SearchBarSearchTipsDialog(props) {
    const {onClickOutside, onClose, visible, buttonRef} = props;
    const wrapperRef = useRef(null);

    useOutsideAlerter(wrapperRef, buttonRef, onClickOutside);

    useEffect(() => {
        if (visible && wrapperRef.current) {
            wrapperRef.current.focus();
        }
    }, [visible]);

    return (
        <div
          id="searchBarSearchTipsDialog"
          ref={wrapperRef}
          role="dialog"
          aria-label="Search tips"
          tabIndex={-1}
          style={props.style}
          onKeyDown={(e) => {
              if (e.key === "Escape") {
                  onClose();
              }
          }}
          className="searchBarOverlayDialog searchBarSearchTipsDialog">
          <strong>Search Tips</strong>
          <p>
            Use the search bar function to input any research terms for a
            full-text search. You can also select from a QUICK SEARCH FIELD
            list above the search bar to explore within categories such as
            Mission, Instrument, and Dataset DOI. Click on the "All Fields"
            dropdown menu to see the full list of available search categories.
            After you select a QUICK SEARCH FIELD category, it will be inserted
            into the search bar automatically. There, you can fill in a
            specific search term (such as "MMS" or "Magnetospheric Multiscale
            Mission" for that mission's data) within the quotation marks that
            appear next to the field name. For a full walkthrough of finding
            and downloading data, see the{" "}
            <Link to="/downloading-and-acquiring">Step-by-Step User Guide</Link>.
          </p>
        </div>
    );
}


/**
 * Helper function that gets the field info content of the All Fields overlay
 * dialog. This is displayed in the right portion of the dialog.
 *
 * @param fieldName (name of field get JSX info for)
 * @returns JSX element (empty if field not recognized)
 */
function getAllFieldsInfoForField(fieldName) {
    let fieldInfo = <div />;

    if (fieldName === "has_api") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">has_api:"true"</span><br /><br />
              Search for datasets that have an API capability.
            </div>
        );
    }
    else if (fieldName === "authors") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">authors:"Baker"</span><br /><br />
              Search for datasets by author name.
            </div>
        );
    }
    else if (fieldName === "cadence_cat") {
        const validValues = ["microseconds", "milliseconds", "seconds", "minutes",
                             "hours", "days", "months", "years"];
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">cadence_cat:"seconds"</span><br /><br />
              Search for datasets by time scale category of dataset cadence.
              For example, a cadence of 5 seconds is grouped under the time scale
              category of "seconds".<br /><br />
              Valid values are:
              <ul>{validValues.map(value => <li key={value}>{value}</li>)}</ul>
            </div>
        );
    }
    else if (fieldName === "doi") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">doi:"https://doi.org/10.48322/1rpt-0w56"</span><br /><br />
	      Search for datasets by their dataset&nbsp;
              <a href="https://www.doi.org/" target="_blank" rel="noreferrer">
                Digital Object Identifier (DOI)
              </a>.
            </div>
        );
    }
    else if (fieldName === "description") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">description:"uranus"</span><br /><br />
              Search for datasets by their dataset description.
            </div>
        );
    }
    else if (fieldName === "has_ftp") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">has_ftp:"true"</span><br /><br />
              Search for datasets that have an FTP capability.
              </div>
        );
    }
    else if (fieldName === "has_heliocloud_s3") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">has_heliocloud_s3:"true"</span><br /><br />
              Search for datasets in HelioCloud S3.
              </div>
        );
    }
    else if (fieldName === "has_https") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">has_https:"true"</span><br /><br />
              Search for datasets that have an HTTPS download capability.
            </div>
        );
    }
    else if (fieldName === "instrument") {
	fieldInfo = (
	    <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">instrument:"EPAM"</span><br /><br />
              Search for datasets from a specific instrument, using the acronym for that instrument.
            </div>
	);
    }
    else if (fieldName === "mission") {
	fieldInfo = (
	    <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">mission:"MMS"</span><br /><br />
              Search for datasets from a specific mission, using the acronym for that mission.
            </div>
	);
    }
    else if (fieldName === "region") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">
                        region:"sun.chromosphere."
                      </span><br />
              Syntax: <span className="searchBarAllFieldsDialogCode">
                        region:"chromosphere"
                      </span><br /><br />

              Search for datasets by their associated scientific regions. Searching for a region automatically
              searches its subregions.<br /><br />

              <b>Valid regions are:</b><br/>
              chromosphere, corona, earth-atmosphere, earth-auroral-region, earth-inner-magnetosphere,
              earth-ionosphere, earth-lower-atmosphere, earth-magnetosheath, earth-magnetosphere, earth-magnetotail,
              earth-outer-magnetosphere, earth-thermosphere, heliosheath, interplanetary-space, jupiter,
              mars, neptune, photosphere, planetary-magnetospheres, saturn, solar-interior, solar-wind, sun,
              uranus
            </div>
        );

    }
    else if (fieldName === "title") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">
                        title:"SHARP Near-Real-Time Cylindrical-Equal-Area Data"
                      </span><br /><br />
              Search for datasets by their dataset title.

            </div>
        );
    }
    else if (fieldName === "variables") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">
                        variables:"temperature"
                      </span><br /><br />
              Search for datasets through their variable names. This searches the human
              readable labels for variables, not the coded names in files or APIs.

            </div>
        );
    }
    else if (fieldName === "variable_shortnames") {
        fieldInfo = (
            <div>
              Syntax: <span className="searchBarAllFieldsDialogCode">
                        variable_shortnames:"mms1_des_dist_brst"
                      </span><br /><br />
              Search for datasets through their variable names. This searches the coded
              "short names" in files and APIs, but not the human readable labels.

            </div>
        );
    }

    return fieldInfo;
}


/**
 * "Advanced" Button on Search Bar that displays an adanced dialog.
 *
 * @param props.centered (Whether or not to display centered)
 * @return JSX element
 */
function SearchBarAdvancedButton(props) {
    const {centered} = props;
    const [advancedVisible, setAdvancedVisible] = useState(false);
    const buttonRef = useRef(null);

    fontAwesomeLibrary.add(faQuestionCircle);

    return (
        <span
          title="Display Advanced Search Options"
          ref={buttonRef}>
          <span
            style={{float: centered ? "" : "right"}}
            className="searchBarAdvanced"
            onClick={() => setAdvancedVisible(!advancedVisible)} >
            <FontAwesomeIcon icon={faQuestionCircle} />&nbsp;&nbsp;ADVANCED
          </span>

          <SearchBarAdvancedDialog
            style={{display: advancedVisible ? "" : "none"}}
            buttonRef={buttonRef}
            onClickOutside={() => setAdvancedVisible(false)} />
        </span>
    );
}

/**
 * Component that alerts if you click outside of it
 *
 * @param props.onClickOutside (Callback function for when user clicks outside this
 * dialog)
 * @returns JSX element
 */
function SearchBarAdvancedDialog(props) {
    const {onClickOutside, buttonRef} = props;
    const wrapperRef = useRef(null);

    useOutsideAlerter(wrapperRef, buttonRef, onClickOutside);

    return (
        <div
          ref={wrapperRef}
          style={props.style}
          className="searchBarOverlayDialog searchBarAdvancedDialog">

          <strong>Advanced Options</strong>

          <ul>
            <li>
              <div>
                Enclose keywords in quotes to search full phrases. <br />
                <em>Example:</em>&nbsp;
                <span className="searchBarAdvancedDialogCode">
                  "radiation belt" flux
                </span>
              </div>
            </li>

            <li>
              <div>Place a hyphen before a keyword to exclude it.<br />
                <em>Example:</em>&nbsp;
                <span className="searchBarAdvancedDialogCode">
                  uranus -ephemeris
                </span>
              </div>
            </li>

            {/*
            <li>
              <div>Use operators AND/OR with optional parenthesis to perform logical operations.<br />
                <em>Example:</em>&nbsp;
                <span className="searchBarAdvancedDialogCode">
                  ions AND (helium OR oxygen)
                </span>
              </div>
            </li>
           */}

            <li>
              <div>Specify field names separated by a colon to search individual fields.<br />
                <em>Example:</em>&nbsp;
                <span className="searchBarAdvancedDialogCode">
                  ACE variables:”helium”
                </span>
              </div>
            </li>
          </ul>
        </div>
    );
}


/**
 * Hook that triggers callback when user clicks outside of the passed refs
 *
 * @param wrapperRef Reference to the dialog being shown
 * @param buttonRef Reference to the button to down it
 * @param onClickOutside Callback function
 */
function useOutsideAlerter(wrapperRef, buttonRef, onClickOutside) {
    useEffect(function() {
        // Wrap onClickOutside with condition that checks that clicks is
        // outside the target element
        function handleClickOutside(event) {
            const notInDialog = wrapperRef.current && !wrapperRef.current.contains(event.target);
            const notInButton = buttonRef.current && !buttonRef.current.contains(event.target);

            if (notInDialog && notInButton) {
                onClickOutside();
            }
        }
        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);

        return function() {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef, buttonRef, onClickOutside]);
}

